import { useMemo, useState } from "react";
import {
  bookingDaysIn,
  browserTimeZone,
  timeZoneChoices,
  timeZoneOptionLabel,
} from "../../../Helper/BookingTimes";
import {
  BookingDay,
  BookingField,
  BookingWidget,
  ChatWidgetText,
  isSafeWidgetImage,
  isSafeWidgetUrl,
} from "../../../Helper/ChatWidget";
import { useAppModalViewStore } from "../../../Stores/AppModalView";
import { useConfigStore } from "../../../Stores/Config";
import { ArrowUpRightIcon } from "../ChatWidgetIcons";
import {
  Button,
  Card,
  ChatWidgetAnswer,
  cx,
  Foot,
  openWidgetUrl,
  Receipt,
} from "../ChatWidgetPrimitives";
import "./BookingCard.scss";
import "./FormCard.scss";

/** An answered pick the server never followed up on stops spinning after this. */
const PENDING_TIMEOUT_MS = 2 * 60 * 1000;

const PROVIDER_NAMES = { calendly: "Calendly", calcom: "Cal.com" };

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const slotName = (day: BookingDay, slotLabel: string) =>
  `${day.label} ${day.dayNumber}, ${slotLabel}`;

const findSlot = (days: BookingDay[], start?: string) => {
  for (const day of days) {
    const slot = day.slots.find((entry) => entry.start === start);
    if (slot) return { day, slot };
  }
  return undefined;
};

const FAILURE_KEYS = {
  slot_unavailable: "widgetSlotUnavailable",
  no_times: "widgetNoTimesLeft",
  email_missing: "widgetBookingEmailMissing",
  provider_error: "widgetBookingTemporary",
  not_available: "widgetBookingNotAvailable",
  rate_limited: "widgetBookingRateLimited",
  fields_required: "widgetBookingFieldsRequired",
  needs_booking_page: "widgetBookingNeedsPage",
} as const;

const failureText = (widget: BookingWidget, text: ChatWidgetText) => {
  const booking = widget.booking;
  if (booking?.status !== "failed") return undefined;
  const key =
    booking.reason && booking.reason in FAILURE_KEYS
      ? FAILURE_KEYS[booking.reason as keyof typeof FAILURE_KEYS]
      : undefined;
  if (key) return text(key);
  return booking.message || text("widgetBookingFailed");
};

/** A phone number as people type it (the server checks the same). */
const isPlausiblePhone = (value: string) =>
  /^\+?[0-9 ()\-.]{6,30}$/.test(value.trim()) &&
  value.replace(/\D/g, "").length >= 6;

const isAnswered = (field: BookingField, value?: string) =>
  !!value?.trim() && (field.type !== "phone" || isPlausiblePhone(value));

const timeLanguage = () => {
  try {
    return useConfigStore.getState().getTimeLanguage?.();
  } catch {
    return undefined;
  }
};

const Host = ({ widget }: { widget: BookingWidget }) => {
  const host = widget.host;
  const title = widget.title ?? host?.name;
  const detail = widget.subtitle ?? widget.durationLabel ?? host?.role;
  return (
    <div className="cw-booking-host">
      <span className="cw-booking-avatar" aria-hidden="true">
        {isSafeWidgetImage(host?.avatar) ? (
          <img
            src={host.avatar}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          initials(host?.name ?? "")
        )}
      </span>
      <div className="cw-stack">
        {title && <span className="cw-strong">{title}</span>}
        {detail && <span className="cw-muted cw-sm">{detail}</span>}
      </div>
    </div>
  );
};

/**
 * Calendly without its Scheduling API: the customer finishes on the
 * pre-filled Calendly page; its `calendly.event_scheduled` goes back to the
 * server as a `confirm` answer, which moves the card to `booked`.
 */
const FinishInProvider = ({
  widget,
  slot,
  onAnswer,
  text,
}: {
  widget: BookingWidget;
  slot?: string;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  const openApp = useAppModalViewStore((state) => state.openApp);
  const closeApp = useAppModalViewStore((state) => state.closeApp);
  const [isScheduled, setIsScheduled] = useState(false);
  const provider =
    PROVIDER_NAMES[widget.provider ?? "calendly"] ?? PROVIDER_NAMES.calendly;
  const url = widget.booking?.url;

  if (isScheduled) {
    return (
      <Receipt
        tone="pending"
        title={text("widgetBookingPending", { slot: slot ?? "" })}
        detail={widget.host?.name}
      />
    );
  }

  const finish = () => {
    if (!isSafeWidgetUrl(url)) return;
    openApp(url, provider, undefined, (data) => {
      if (data?.event !== "calendly.event_scheduled") return;
      const eventUri = data.payload?.event?.uri;
      setIsScheduled(true);
      onAnswer(
        "confirm",
        typeof eventUri === "string" ? { eventUri } : {},
        slot ?? text("widgetBooked"),
        { kind: "followUp" },
      );
      closeApp();
    });
  };

  return (
    <Card className="cw-booking" label={widget.title ?? widget.host?.name}>
      <div className="cw-pad">
        <Host widget={widget} />
        {widget.booking?.reason === "not_matched" && (
          <div className="cw-booking-notice cw-sm" role="alert">
            {text("widgetBookingNotMatched", { provider })}
          </div>
        )}
        <div className="cw-sm cw-body cw-booking-hint">
          {text("widgetFinishBookingHint", { slot: slot ?? "", provider })}
        </div>
      </div>
      {isSafeWidgetUrl(url) && (
        <Foot flush>
          <Button onClick={finish}>
            <span>{text("widgetFinishBooking", { provider })}</span>
          </Button>
        </Foot>
      )}
    </Card>
  );
};

/** The card's open state when no time can be picked: the booking page, or the team. */
const NoTimes = ({
  widget,
  text,
  notice,
}: {
  widget: BookingWidget;
  text: ChatWidgetText;
  notice: string;
}) => {
  const url = isSafeWidgetUrl(widget.booking?.url)
    ? widget.booking?.url
    : widget.bookingUrl;
  return (
    <Card className="cw-booking" label={widget.title ?? widget.host?.name}>
      <div className="cw-pad">
        <Host widget={widget} />
        <div className="cw-booking-notice cw-sm" role="alert">
          {notice}
          {!isSafeWidgetUrl(url) && ` ${text("widgetAskTheTeam")}`}
        </div>
      </div>
      {isSafeWidgetUrl(url) && (
        <Foot flush>
          <Button onClick={() => openWidgetUrl(url)}>
            <span>{text("widgetOpenBookingPage")}</span>
            <ArrowUpRightIcon size={13} />
          </Button>
        </Foot>
      )}
    </Card>
  );
};

/**
 * 06 Book a time: day tabs → slot grid → "Book Wed 15, 11:30". Times show in
 * the customer's browser time zone (switchable); the pick carries the zone
 * and the answers to the event type's required questions. The server books
 * it with Calendly / Cal.com and writes the outcome to `booking` (booking →
 * booked / requested, or confirm_in_provider, or failed: the card reopens
 * with fresh slots and a note, or without slots when none are left).
 */
export const BookingCard = ({
  widget,
  onAnswer,
  text,
}: {
  widget: BookingWidget;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  const [timeZone, setTimeZone] = useState<string | undefined>(
    () => browserTimeZone() ?? widget.timeZone,
  );
  const days = useMemo(() => {
    const serverDays = (widget.days ?? []).map((day) => ({
      ...day,
      slots: day.slots ?? [],
    }));
    // The server labelled the days in its zone for this customer; relabel in another.
    if (!timeZone || timeZone === widget.timeZone) return serverDays;
    try {
      return bookingDaysIn(serverDays, timeZone, timeLanguage());
    } catch {
      return serverDays;
    }
  }, [widget.days, widget.timeZone, timeZone]);
  const zones = useMemo(
    () => timeZoneChoices([timeZone, browserTimeZone(), widget.timeZone]),
    [timeZone, widget.timeZone],
  );
  const firstOpenDay = Math.max(
    0,
    days.findIndex((day) => day.slots.length > 0),
  );
  const [dayIndex, setDayIndex] = useState(firstOpenDay);
  const [slotStart, setSlotStart] = useState<string | undefined>();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const booking = widget.booking;
  const inviteEmail = booking?.email ?? widget.invitee?.email;
  const fields = widget.fields ?? [];

  if (widget.response) {
    const answered = widget.response.values?.selected;
    const picked = findSlot(
      days,
      booking?.start ?? (Array.isArray(answered) ? answered[0] : undefined),
    );
    const pickedName = picked
      ? slotName(picked.day, picked.slot.label)
      : widget.response.label;

    if (booking?.status === "booked") {
      return (
        <Receipt
          title={text("widgetBooked")}
          detail={[
            booking.label ?? pickedName,
            inviteEmail
              ? text("widgetInviteSentTo", { email: inviteEmail })
              : undefined,
          ]
            .filter(Boolean)
            .join(" · ")}
        />
      );
    }
    if (booking?.status === "requested") {
      return (
        <Receipt
          tone="neutral"
          title={text("widgetBookingRequested", {
            host: booking.host ?? widget.host?.name ?? "",
          })}
          detail={[booking.label ?? pickedName, widget.durationLabel]
            .filter(Boolean)
            .join(" · ")}
        />
      );
    }
    if (booking?.status === "confirm_in_provider") {
      return (
        <FinishInProvider
          widget={widget}
          slot={pickedName}
          onAnswer={onAnswer}
          text={text}
        />
      );
    }
    const answeredAt = new Date(widget.response.at ?? Date.now()).getTime();
    const isStale = Date.now() - answeredAt > PENDING_TIMEOUT_MS;
    return (
      <Receipt
        tone={isStale ? "neutral" : "pending"}
        title={
          isStale
            ? pickedName
            : text("widgetBookingPending", { slot: pickedName })
        }
        detail={[widget.host?.name, widget.durationLabel]
          .filter(Boolean)
          .join(" · ")}
      />
    );
  }

  const failure = failureText(widget, text);
  if (!days.some((entry) => entry.slots.length > 0)) {
    return (
      <NoTimes
        widget={widget}
        text={text}
        notice={failure ?? text("widgetNoTimesLeft")}
      />
    );
  }

  const day = days[dayIndex] ?? days[0];
  // Only a time on the shown day is the pick (another day's selection is gone).
  const picked = findSlot(day ? [day] : [], slotStart);
  const canBook =
    !!picked && fields.every((field) => isAnswered(field, answers[field.name]));
  const book = () => {
    if (!picked || !canBook) return;
    const values: Record<string, string | string[]> = {
      selected: [picked.slot.start],
    };
    if (timeZone) values.timeZone = timeZone;
    for (const field of fields) values[field.name] = answers[field.name].trim();
    onAnswer("select", values, slotName(picked.day, picked.slot.label));
  };
  const changeTimeZone = (zone: string) => {
    setTimeZone(zone);
    setSlotStart(undefined);
    setDayIndex(0);
  };
  const groupLabel = widget.title ?? widget.host?.name;

  return (
    <Card className="cw-booking" label={groupLabel}>
      <div className="cw-pad">
        <Host widget={widget} />
        {failure && (
          <div className="cw-booking-notice cw-sm" role="alert">
            {failure}
          </div>
        )}
        <div
          className="cw-booking-days"
          role="tablist"
          aria-label={groupLabel}
          style={{
            ["--cw-days" as string]: Math.max(1, Math.min(days.length, 4)),
          }}
        >
          {days.map((entry, index) => (
            <button
              type="button"
              role="tab"
              key={entry.date}
              aria-selected={index === dayIndex}
              disabled={entry.slots.length === 0}
              className={cx(
                "cw-booking-day",
                index === dayIndex && "cw-booking-day--on",
              )}
              onClick={() => {
                if (index !== dayIndex) setSlotStart(undefined);
                setDayIndex(index);
              }}
            >
              <span>{entry.label}</span>
              <b className="cw-num">{entry.dayNumber}</b>
            </button>
          ))}
        </div>
        {day && (
          <div className="cw-booking-slots" role="tabpanel">
            {day.slots.map((slot) => (
              <button
                type="button"
                key={slot.start}
                aria-pressed={slot.start === slotStart}
                className={cx(
                  "cw-booking-slot",
                  slot.start === slotStart && "cw-booking-slot--on",
                )}
                onClick={() => setSlotStart(slot.start)}
              >
                {slot.label}
              </button>
            ))}
          </div>
        )}
        <div className="cw-row cw-booking-meta">
          {timeZone ? (
            <select
              className="cw-muted cw-sm cw-booking-zone"
              aria-label={text("widgetTimezone")}
              value={timeZone}
              onChange={(event) => changeTimeZone(event.target.value)}
            >
              {zones.map((zone) => (
                <option key={zone} value={zone}>
                  {text("widgetTimesIn", {
                    timezone: timeZoneOptionLabel(zone),
                  })}
                </option>
              ))}
            </select>
          ) : (
            <span className="cw-muted cw-sm">
              {widget.timezoneLabel
                ? text("widgetTimesIn", { timezone: widget.timezoneLabel })
                : ""}
            </span>
          )}
          {isSafeWidgetUrl(widget.bookingUrl) && (
            <button
              type="button"
              className="cw-booking-more"
              onClick={() => openWidgetUrl(widget.bookingUrl)}
            >
              {text("widgetMoreTimes")}
              <ArrowUpRightIcon size={13} />
            </button>
          )}
        </div>
        {fields.length > 0 && (
          <div className="cw-form-group cw-booking-fields">
            {fields.map((field) => {
              const id = `cw-${widget.id}-${field.name}`;
              const value = answers[field.name] ?? "";
              const onChange = (next: string) =>
                setAnswers((current) => ({ ...current, [field.name]: next }));
              return (
                <div className="cw-form-field" key={field.name}>
                  <label htmlFor={id}>
                    {field.label}
                    <span
                      className="cw-form-required"
                      aria-label={text("widgetRequired")}
                    >
                      {" *"}
                    </span>
                  </label>
                  {field.type === "textarea" ? (
                    <textarea
                      id={id}
                      className="cw-form-control"
                      value={value}
                      required
                      rows={2}
                      onChange={(event) => onChange(event.target.value)}
                    />
                  ) : (
                    <input
                      id={id}
                      className="cw-form-control"
                      type={field.type === "phone" ? "tel" : "text"}
                      autoComplete={field.type === "phone" ? "tel" : "off"}
                      value={value}
                      required
                      aria-invalid={
                        field.type === "phone" &&
                        value.trim().length > 0 &&
                        !isPlausiblePhone(value)
                      }
                      onChange={(event) => onChange(event.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {inviteEmail && (
          <div className="cw-muted cw-sm cw-booking-invitee">
            {text("widgetInviteGoesTo", { email: inviteEmail })}
          </div>
        )}
      </div>
      <Foot flush>
        <Button disabled={!canBook} onClick={book}>
          <span>
            {picked
              ? text("widgetBook", {
                  slot: slotName(picked.day, picked.slot.label),
                })
              : text("widgetPickTime")}
          </span>
        </Button>
      </Foot>
    </Card>
  );
};
